import { useCallback, useEffect, useRef, useState } from 'react';
import { ConcurrentSaveError } from '../util/errors';

/**
 * Encapsulates concurrent save error state management.
 *
 * ## Parent orchestration contract
 *
 * This hook requires specific calls from the parent component to function correctly:
 * - Call `undismiss()` when opening the save dialog — this re-shows a previously dismissed
 *   error so the user sees it again when they attempt another save.
 * - Call `dismiss()` when closing/canceling the save dialog — this hides the error and
 *   resets the force-overwrite checkbox.
 *
 * If either call is omitted:
 * - Missing `undismiss()`: user opens save dialog but stale concurrent error stays hidden
 * - Missing `dismiss()`: error persists visually after dialog close
 *
 * Cross-page behavior: when the user dismisses the error on page A and navigates
 * to page B, the ref initializes with the same error object reference so the
 * `!==` check is false, keeping `isDismissed` true. The error only becomes
 * visible again when the user explicitly calls `undismiss()` (e.g. by clicking Save).
 */
export function useConcurrentSaveError(concurrentSaveError: ConcurrentSaveError | null | undefined) {
    const [forceOverwrite, setForceOverwrite] = useState(false);
    const [isDismissed, setIsDismissed] = useState(concurrentSaveError != null);

    // Track the previous value so we only un-dismiss on genuinely *new* errors.
    // Without the ref, the effect would fire on mount with a stale error from Redux
    // (e.g. after navigating away and back), incorrectly reopening the modal.
    const prevErrorRef = useRef(concurrentSaveError);
    useEffect(() => {
        if (concurrentSaveError != null && concurrentSaveError !== prevErrorRef.current) {
            setIsDismissed(false);
        }
        prevErrorRef.current = concurrentSaveError;
    }, [concurrentSaveError]);

    const isActive = concurrentSaveError != null && !isDismissed;

    const dismiss = useCallback(() => { setIsDismissed(true); setForceOverwrite(false); }, []);
    const undismiss = useCallback(() => { setIsDismissed(false); }, []);

    return { forceOverwrite, setForceOverwrite, isActive, dismiss, undismiss };
}
